import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNativeBridge } from '@dhl/native-bridge'
import {
  useGetNotificationPreferences,
  usePostNotificationPreferencesPushNotificationPreferences,
} from '@dhl/receiver-profile-api'
import { isFeatureEnabledNative } from '~/config/index.js'
import { links } from '~/links.js'

const isPushNotificationFeatureActive = () => isFeatureEnabledNative('profile.notificationPreferences.push')

type Props = {
  isEnabled: boolean
}

export const usePushNotificationsSync = ({ isEnabled }: Props): void => {
  const {
    requestPushNotificationPermissions,
    isDisablePushNotificationRequested,
    acknowledgeDisablePushNotifications,
    isDeniedPushNotificationPermission,
  } = useNativeBridge()
  const navigate = useNavigate()

  const { data: notificationPreferences, refetch: refetchNotificationPreferences } = useGetNotificationPreferences({
    query: {
      enabled: isPushNotificationFeatureActive(),
    },
  })
  const { mutate: updatePushNotificationPreference } = usePostNotificationPreferencesPushNotificationPreferences()

  // This always runs, so permission is checked upon boot, no matter on which URL (except guided tour) the native app starts (incoming links)
  useEffect(() => {
    if (notificationPreferences && isEnabled) {
      const pushPrefs = notificationPreferences?.pushNotificationPreference
      if (pushPrefs.isEnabledForIncomingParcels || pushPrefs.isEnabledForOutgoingParcels) {
        requestPushNotificationPermissions()
      }
    }
  }, [requestPushNotificationPermissions, notificationPreferences, isEnabled])

  useEffect(() => {
    if (isDeniedPushNotificationPermission && isEnabled) {
      navigate(links.applicationLink())
    }
  }, [isDeniedPushNotificationPermission, isEnabled, navigate])

  // // This always runs, so the web layer can complete the permission synchronisation
  useEffect(() => {
    if (isDisablePushNotificationRequested && isPushNotificationFeatureActive()) {
      updatePushNotificationPreference(
        {
          data: {
            enabledForIncomingParcels: false,
            enabledForOutgoingParcels: false,
          },
        },
        {
          onSuccess: () => {
            acknowledgeDisablePushNotifications()
            refetchNotificationPreferences()
          },
        }
      )
    }
  }, [
    acknowledgeDisablePushNotifications,
    isDisablePushNotificationRequested,
    refetchNotificationPreferences,
    updatePushNotificationPreference,
  ])
}
